// Full-document render of the tempurpedic OfferDetailPage: PageSkeleton (the
// React document shell) wrapping the offer-details layout (normal site
// chrome).
//
// Load-bearing: the content StreamField renders with every top-level block
// wrapped in `<div class="w-block-{type} block-{type}">` -- offer-details.scss
// keys on `.block-table` -- reproduced per top-level block via the per-block
// `StreamField tagName="div"` render (the LegalPage house pattern). The
// breadcrumb/header/back-link DOM around the content is rebuilt byte-faithfully;
// the two labels (`offers_label`/`back_label`) arrive server-resolved since
// React ttag has no runtime locale.
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    AnchorBlock as AnchorBlockValue,
    RichTextBlock as RichTextBlockValue,
    TableBlock as TableBlockValue,
    TempurFinanceFAQsBlock as TempurFinanceFAQsBlockValue,
    TypedTableBlock as TypedTableBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { TempurFinanceFAQsBlock } from "../blocks/TempurFinanceFAQsBlock";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.OfferDetailPageTemplate;

// OfferDetailContent block set:
//   - `text` (RichTextBlock) -> the server-rendered rich-text HTML string;
//   - `table` / `typed_table` (thelabui / local ClientRenderingBarrier) -> the
//     server-rendered wagtail table HTML string, raw-injected;
//   - `anchor_link` (RegexBlock) -> renders `<span id="{value}">`; the value
//     is the raw anchor-id string;
//   - `FAQs` (TempurFinanceFAQsBlock) -> the shared accordion component.
const contentMap = {
    text: (block: { value: Immutable<RichTextBlockValue> }) => (
        <div dangerouslySetInnerHTML={{ __html: block.value }} />
    ),
    table: (block: { value: Immutable<TableBlockValue> }) =>
        block.value ? (
            <div dangerouslySetInnerHTML={{ __html: block.value }} />
        ) : null,
    typed_table: (block: { value: Immutable<TypedTableBlockValue> }) =>
        block.value ? (
            <div dangerouslySetInnerHTML={{ __html: block.value }} />
        ) : null,
    anchor_link: (block: { value: Immutable<AnchorBlockValue> }) => (
        <span id={block.value}></span>
    ),
    FAQs: (block: { value: Immutable<TempurFinanceFAQsBlockValue> }) => (
        <TempurFinanceFAQsBlock value={block.value} />
    ),
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page } = props;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div
                className={`offer-details-page offer-details-page--${page.offer_type} u-full-width-title l-capped-width`}
            >
                <div className="offer-details-page__breadcrumbs">
                    <a href={props.offers_url}>{props.offers_label}</a>
                    {page.header_text && (
                        <>
                            {" > "}
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: page.header_text,
                                }}
                            />
                        </>
                    )}
                </div>
                <header className="offer-details-page__header">
                    {props.pre_header_html && (
                        <div>
                            <h6
                                dangerouslySetInnerHTML={{
                                    __html: props.pre_header_html,
                                }}
                            />
                        </div>
                    )}
                    {page.header_text && (
                        <h1
                            dangerouslySetInnerHTML={{
                                __html: page.header_text,
                            }}
                        />
                    )}
                    <div className="offer-details-page__bar"></div>
                </header>

                <section className="offer-details-page__content">
                    {page.content.value.map((block) => (
                        <StreamField
                            key={block.id}
                            tagName="div"
                            attrs={{
                                className: `w-block-${block.type} block-${block.type}`,
                            }}
                            value={[block]}
                            components={contentMap}
                        />
                    ))}
                </section>
                <a href={props.offers_url}>
                    <div className="offer-details-page__back-link">
                        {"< "}
                        {props.back_label}
                    </div>
                </a>
            </div>
        </PageSkeleton>
    );
};
